import React, { useState } from "react";
import Swal from "sweetalert2";
import { API_BASE } from "../config";
import { apiFetch } from "../utilities/apiFetch";
import { fechaASerialExcel } from "../utilities/fechaExcel";

// Primer dia del mes corriente y hoy, en formato AAAA-MM-DD
const primerDiaDelMes = () => {
  const hoy = new Date();
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-01`;
};

const hoyISO = () => {
  const hoy = new Date();
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}-${String(hoy.getDate()).padStart(2, "0")}`;
};

const VistaReportes = () => {
  const [fechaInicio, setFechaInicio] = useState(primerDiaDelMes());
  const [fechaFin, setFechaFin] = useState(hoyISO());
  // 'AAAA-MM-DD' -> numero de serie de Excel (ver utilities/fechaExcel.js: con un Date local salia el dia anterior)
  const aFecha = fechaASerialExcel;
  const [descargando, setDescargando] = useState("");

  const rol = String(localStorage.getItem("cajero_rol") || "");
  const idPlantel = String(localStorage.getItem("cajero_plantel") || "");
  const esAdmin = rol === "1";

  const consultar = async (tipo) => {
    const params = new URLSearchParams({
      tipo,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      rol,
      id_plantel: idPlantel
    });

    const res = await apiFetch(`${API_BASE}/wMultipagosReportes.php?${params.toString()}`);
    return res.json();
  };

  // --- REPORTE 1: detalle general ---
  const descargarGeneral = async () => {
    setDescargando("general");
    try {
      const resultado = await consultar("general");

      if (!resultado.success) {
        Swal.fire("No se pudo generar", resultado.message, "error");
        return;
      }

      if (resultado.data.length === 0) {
        Swal.fire("Sin resultados", "No hay movimientos en el rango de fechas seleccionado.", "info");
        return;
      }

      const filas = resultado.data.map((r) => ({
        "ID Transacción": r.id ? parseInt(r.id, 10) : "",
        "Fecha efectiva": aFecha(r.fecha_efectiva),
        "Estado conciliación": r.estado_conciliacion,
        "Estatus": r.estatus,
        "Plantel": r.plantel,
        "Número plantel": r.numero_plantel,
        "Alumno / Matrícula": r.codigo_alumno,
        "Banco": r.banco,
        "Método de pago": r.metodo_pago,
        "Conceptos": r.conceptos || "",
        "Importe total": parseFloat(r.importe_total || 0),
        "Importe pagado": r.importe_pagado !== null && r.importe_pagado !== "" ? parseFloat(r.importe_pagado) : "",
        "Ref. Banorte": r.referencia_banorte || "",
        "Ref. BBVA": r.referencia_bbva || "",
        "Ref. pagada (corregida)": r.referencia_pagada || "",
        "Folio registro": r.folio_registro || "",
        "Folio autorización": r.folio_banco || "",
        "Motivo discrepancia": r.motivo_discrepancia || "",
        "Complemento de": r.id_intencion_origen ? parseInt(r.id_intencion_origen, 10) : "",
        "Fecha creación": aFecha(r.fecha_creacion),
        "Hora creación": r.hora_creacion || "",
        "Fecha validación": aFecha(r.fecha_validacion),
        "Hora validación": r.hora_validacion || "",
        "Fecha pago banco": aFecha(r.fecha_pago),
        "Cajero": r.nombre_cajero || r.id_cajero || ""
      }));

      // La libreria de Excel pesa mucho: se carga solo al exportar un reporte.
      const XLSX = await import("xlsx");

      const hoja = XLSX.utils.json_to_sheet(filas);
      hoja["!cols"] = Object.keys(filas[0]).map((k) => ({ wch: Math.max(k.length + 2, 14) }));

      // Formato de fecha y moneda en las columnas que corresponde
      const encabezados = Object.keys(filas[0]);
      const rango = XLSX.utils.decode_range(hoja["!ref"]);

      for (let col = rango.s.c; col <= rango.e.c; col++) {
        const titulo = encabezados[col];
        const esFecha = titulo.startsWith("Fecha");
        const esMoneda = titulo.startsWith("Importe");
        if (!esFecha && !esMoneda) continue;

        for (let fila = 1; fila <= rango.e.r; fila++) {
          const celda = hoja[XLSX.utils.encode_cell({ r: fila, c: col })];
          if (!celda || celda.v === "") continue;
          celda.z = esFecha ? "dd/mm/yyyy" : '"$"#,##0.00';
        }
      }

      const libro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(libro, hoja, "Movimientos");

      XLSX.writeFile(libro, `Multipagos_General_${fechaInicio}_a_${fechaFin}.xlsx`);

    } catch (error) {
      console.error("Error al generar el reporte:", error);
      Swal.fire("Error", "Ocurrió un problema al generar el reporte.", "error");
    } finally {
      setDescargando("");
    }
  };

  // --- REPORTE 2: totales por concepto, una hoja por banco ---
  const descargarConceptos = async () => {
    setDescargando("conceptos");
    try {
      const resultado = await consultar("conceptos");

      if (!resultado.success) {
        Swal.fire("No se pudo generar", resultado.message, "error");
        return;
      }

      if (resultado.data.length === 0) {
        Swal.fire("Sin resultados", "No hay movimientos en el rango de fechas seleccionado.", "info");
        return;
      }

      const armarHoja = (registros) =>
        registros.map((r) => ({
          "Banco": r.banco,
          "Plantel": r.plantel,
        "Número plantel": r.numero_plantel,
          "Clave": r.clave_concepto,
          "Concepto": r.concepto,
          "Total": parseFloat(r.total || 0)
        }));

      const XLSX = await import("xlsx");
      const libro = XLSX.utils.book_new();
      const bancos = ["BBVA", "Banorte"];

      bancos.forEach((nombreBanco) => {
        const registros = resultado.data.filter(
          (r) => String(r.banco).toUpperCase() === nombreBanco.toUpperCase()
        );

        const filas = registros.length > 0
          ? armarHoja(registros)
          : [{ "Banco": nombreBanco, "Plantel": "", "Número plantel": "", "Clave": "", "Concepto": "Sin movimientos", "Total": 0 }];

        const hoja = XLSX.utils.json_to_sheet(filas);
        hoja["!cols"] = [{ wch: 12 }, { wch: 26 }, { wch: 13 }, { wch: 10 }, { wch: 45 }, { wch: 14 }];

        // Total como moneda
        const rango = XLSX.utils.decode_range(hoja["!ref"]);
        for (let fila = 1; fila <= rango.e.r; fila++) {
          const celda = hoja[XLSX.utils.encode_cell({ r: fila, c: 5 })];
          if (celda) celda.z = '"$"#,##0.00';
        }

        XLSX.utils.book_append_sheet(libro, hoja, nombreBanco);
      });

      // Si hubiera movimientos con otro banco, no se pierden
      const otros = resultado.data.filter(
        (r) => !bancos.some((b) => b.toUpperCase() === String(r.banco).toUpperCase())
      );
      if (otros.length > 0) {
        const hojaOtros = XLSX.utils.json_to_sheet(armarHoja(otros));
        XLSX.utils.book_append_sheet(libro, hojaOtros, "Otros");
      }

      XLSX.writeFile(libro, `Multipagos_Conceptos_${fechaInicio}_a_${fechaFin}.xlsx`);

    } catch (error) {
      console.error("Error al generar el reporte:", error);
      Swal.fire("Error", "Ocurrió un problema al generar el reporte.", "error");
    } finally {
      setDescargando("");
    }
  };

  const rangoInvalido = !fechaInicio || !fechaFin || fechaInicio > fechaFin;

  const estiloInput = {
    padding: "10px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "1rem"
  };

  const estiloTarjeta = {
    border: "1px solid #ddd", borderRadius: "8px", padding: "20px",
    backgroundColor: "#fff", boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    display: "flex", flexDirection: "column", justifyContent: "space-between"
  };

  return (
    <div className="Dashboard-card">
      <h3 style={{ marginBottom: "5px", color: "#003b5c" }}>Reportes</h3>
      <p style={{ color: "#666", fontSize: "0.9rem", marginTop: 0 }}>
        Los reportes se descargan en Excel. Por omisión se muestra del día 1 del mes en curso a hoy.
      </p>

      {/* RANGO DE FECHAS */}
      <div style={{
        display: "flex", gap: "15px", alignItems: "flex-end", flexWrap: "wrap",
        padding: "15px", backgroundColor: "#f8f9fa", borderRadius: "6px", margin: "20px 0"
      }}>
        <div>
          <label style={{ display: "block", fontWeight: "bold", marginBottom: "6px", fontSize: "0.85rem" }}>
            Desde
          </label>
          <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} style={estiloInput} />
        </div>
        <div>
          <label style={{ display: "block", fontWeight: "bold", marginBottom: "6px", fontSize: "0.85rem" }}>
            Hasta
          </label>
          <input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} style={estiloInput} />
        </div>
        <button
          onClick={() => { setFechaInicio(primerDiaDelMes()); setFechaFin(hoyISO()); }}
          className="btn-logout"
          style={{ width: "auto", padding: "10px 16px", backgroundColor: "#6c757d" }}
        >
          Mes en curso
        </button>
      </div>

      {rangoInvalido && (
        <div style={{
          marginBottom: "15px", padding: "10px", borderRadius: "4px",
          backgroundColor: "#fff8e1", border: "1px solid #ffe082", color: "#7a5c00", fontSize: "0.85rem"
        }}>
          ℹ️ La fecha inicial no puede ser mayor que la final.
        </div>
      )}

      {/* TARJETAS DE REPORTE */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "20px" }}>

        <div style={estiloTarjeta}>
          <div>
            <h4 className="titulo-seccion">Reporte general</h4>
            <p style={{ fontSize: "0.9rem", color: "#555", margin: "0 0 15px 0" }}>
              Detalle de cada movimiento: alumno, plantel, banco, método de pago, importes,
              referencias y estado de conciliación.
              {!esAdmin && " Incluye únicamente los movimientos de tu plantel."}
            </p>
          </div>
          <button
            onClick={descargarGeneral}
            disabled={rangoInvalido || descargando !== ""}
            className="btn-select validar"
            style={{
              border: "none", padding: "10px",
              opacity: (rangoInvalido || descargando !== "") ? 0.45 : 1,
              cursor: (rangoInvalido || descargando !== "") ? "not-allowed" : "pointer"
            }}
          >
            {descargando === "general" ? "Generando..." : "Descargar Excel"}
          </button>
        </div>

        {esAdmin && (
          <div style={estiloTarjeta}>
            <div>
              <h4 className="titulo-seccion">Totales por concepto</h4>
              <p style={{ fontSize: "0.9rem", color: "#555", margin: "0 0 15px 0" }}>
                Suma de dinero por banco, plantel y concepto. Una hoja para BBVA y otra para Banorte.
              </p>
            </div>
            <button
              onClick={descargarConceptos}
              disabled={rangoInvalido || descargando !== ""}
              className="btn-select validar"
              style={{
                border: "none", padding: "10px",
                opacity: (rangoInvalido || descargando !== "") ? 0.45 : 1,
                cursor: (rangoInvalido || descargando !== "") ? "not-allowed" : "pointer"
              }}
            >
              {descargando === "conceptos" ? "Generando..." : "Descargar Excel"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VistaReportes;